"use client";

import { useBuyBoxFactory } from "@trydig/centra/client";
import type { BaseSelection } from "@trydig/centra-types/selection";
import { Suspense, useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { useCartModal } from "@/features/cart/components/CartModal/CartModal";
import { useIsPreorder } from "@/features/products/hooks";
import { isProductBundle } from "@/features/products/utils";
import { useTranslations } from "@/i18n/client";
import { useGeo } from "@/i18n/routing";
import type { ProductDetails } from "@/lib/centra";
import { gtm } from "@/lib/gtm";
import { selectionItemToGTMProduct } from "@/lib/gtm/utils";
import { klaviyoTracker } from "@/lib/klaviyo";
import styles from "./AddToCartButton.module.css";
import { BackInStock } from "./BackInStock";
import { buildMerchantFeedId } from "./buildMerchantFeedId";
import { useBuyBoxLabels } from "./hooks";

export interface AddToCartButtonProps {
	product: ProductDetails;
	fullWidth?: boolean;
}

const UnsuspendedAddToCartButton = ({
	product,
	fullWidth,
}: AddToCartButtonProps) => {
	const labels = useBuyBoxLabels();
	const t = useTranslations();
	const [_, openModal] = useCartModal();

	const { items: sizes } = product;

	const { country } = useGeo();

	const { data: preorderData } = useIsPreorder({
		centraVariantId: product?.centraVariant,
		isBundle: isProductBundle(product),
		displayItemId: product.displayItemId,
	});

	const { selectSize, getButtonProps, getButtonLabel, state, item } =
		useBuyBoxFactory({
			labels,
			sizes,
			onSuccess: (selection, { itemId }) => {
				openModal(true);
				const selectionItem = selection?.selection?.items?.find(
					(item) => item.item === itemId,
				);

				if (selectionItem && selection?.selection?.currency) {
					gtm.addToCart(
						selectionItemToGTMProduct({
							item: selectionItem,
							currency: selection.selection.currency,
						}),
					);
				}
				klaviyoTracker.addToCart(selection.selection as BaseSelection);
			},
		});

	// Klaviyo catalog id for the selected size, e.g. "1/gb-p295-v2028-s578"
	const merchantFeedId = buildMerchantFeedId(
		product.centraProduct,
		product.centraVariant,
		country?.toLowerCase(),
		item?.item,
	);

	// Auto-select the first size, but only when nothing is selected yet.
	// Guarding on `item` prevents an infinite replaceState loop: selectSize
	// writes `?size=` to the URL unconditionally and is a new reference on
	// every render, so without this guard the effect re-fires on each history
	// change and floods the dataLayer with gtm.historyChange events.
	useEffect(() => {
		if (item || !sizes?.at(0)) return;
		selectSize(sizes[0]);
	}, [item, selectSize, sizes]);

	if (product.outOfStock || state.type === "out_of_stock") {
		if (merchantFeedId) {
			return (
				<BackInStock
					getButtonProps={getButtonProps}
					item={merchantFeedId}
					fullWidth={fullWidth}
				/>
			);
		} else {
			return (
				<Button
					fullWidth={fullWidth}
					{...getButtonProps()}
					data-state="out_of_stock"
					disabled
				>
					{getButtonLabel()}
				</Button>
			);
		}
	}

	if (preorderData) {
		return (
			<div>
				<Button fullWidth={fullWidth} {...getButtonProps()}>
					{labels.preorder}
				</Button>
				<p className={styles.preorder}>
					{t("This is a pre-order item")}, {t("est. delivery")}{" "}
					{preorderData.eta}
				</p>
			</div>
		);
	}

	return (
		<Button
			fullWidth={fullWidth}
			{...getButtonProps()}
			data-state="add_to_cart"
		>
			{getButtonLabel()}
		</Button>
	);
};

export const AddToCartButton: typeof UnsuspendedAddToCartButton = (props) => {
	return (
		<Suspense fallback={<AddToCartButtonSkeleton />}>
			<UnsuspendedAddToCartButton {...props} />
		</Suspense>
	);
};

export const AddToCartButtonSkeleton = () => {
	return (
		<Skeleton height="fit-content" width="100%">
			<Button fullWidth>Add to cart</Button>
		</Skeleton>
	);
};
